<template>
  <div>
    <!-- Delete Modal -->
    <DeleteModal modal-id="modal-delete" @deleteToggle="deleteItem" />

    <!-- Create / Edit Modal -->
    <b-modal
      id="modal-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="createMode ? $t('incubator.Add-Type') : $t('incubator.Edit-Type')"
    >
      <b-card-text>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('common.name')" label-for="name">
              <b-form-input
                id="name"
                v-model="editForm.name"
                :placeholder="$t('common.name')"
              />
              <p class="text-danger mt-1">
                {{ errorForm.name }}
              </p>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
            :label="$t('incubator.price_per_day')"
            label-for="price_per_time_unit"
            >
            <b-form-input
            id="price_per_time_unit"
            v-model="editForm.price_per_time_unit"
            type="number"
            min="0"
            :placeholder="$t('incubator.price_per_day')"
            />
            <p class="text-danger mt-1">
              {{ errorForm.price_per_time_unit }}
            </p>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('common.description')"
            label-for="description"
          >
            <b-form-textarea
              id="description"
              v-model="editForm.description"
              :placeholder="$t('common.description')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
        </b-col>
      </b-row>
      </b-card-text>

      <template #modal-footer="{}">
        <b-button
          ref="button"
          variant="primary"
          class="m-0"
          :disabled="loading"
          block
          @click="submitItem"
        >
          <span v-if="!loading">{{
            createMode ? $t("common.add") : $t("common.save")
          }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>

    <!-- CONTENT (guarded by list permission) -->
    <div v-if="canList">
      <b-card :title="$t('incubator.Types')">
        <!-- Filters -->
        <b-row>
          <b-col cols="12" md="12">
            <h2 class="mb-2">
              {{ $t("incubator.Filter-Types") }}
            </h2>
          </b-col>

          <b-col md="6" cols="12">
            <b-form-group :label="$t('common.name')" label-for="filter_name">
              <b-form-input
                id="filter_name"
                v-model="filters.name"
                :placeholder="$t('common.name')"
                @keyup.enter="getIncubatorTypesList"
              />
            </b-form-group>
          </b-col>

          <b-col md="6" cols="12">
            <b-form-group
              :label="$t('incubator.price_per_day_min')"
              label-for="filter_min_price"
            >
              <b-form-input
                id="filter_min_price"
                v-model="filters.min_price"
                type="number"
                min="0"
                :placeholder="$t('incubator.price_per_day_min')"
                @keyup.enter="getIncubatorTypesList"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Search -->
        <b-row class="mb-2">
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button variant="primary" @click="getIncubatorTypesList">
              {{ $t("common.search") }}
              <b-icon-search class="ml-1" />
            </b-button>
          </b-col>
        </b-row>

        <!-- Header + Add -->
        <div class="d-flex justify-content-between mt-2 mb-2">
          <h2 class="h4 m-0">
            {{ $t("incubator.Types") }}
          </h2>
          <b-button
            v-if="canCreate"
            variant="primary"
            @click="show(null, 'create')"
          >
            {{ $t("incubator.Add-Type") }}
          </b-button>
        </div>

        <!-- Table -->
        <b-table
          v-if="!loading"
          :items="data"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('common.no_results')"
          class="position-relative"
        >
          <!-- price formatting -->
          <template #cell(price_per_time_unit)="ctx">
            <span>{{ formatPrice(ctx.item.price_per_time_unit) }}</span>
          </template>

          <!-- actions -->
          <template #cell(actions)="ctx">
            <div class="text-nowrap">
              <ActionsButtons
                :edit-perm="'update-incubatorType'"
                :delete-perm="'delete-incubatorType'"
                :can-edit="canUpdate"
                :can-delete="canDelete"
                @editToggle="show(ctx.item)"
                @deleteToggle="deleteModel(ctx.item)"
              />
            </div>
          </template>
        </b-table>

        <!-- Loading -->
        <div v-else class="d-flex justify-content-center mb-3">
          <b-spinner label="Loading..." />
        </div>

        <!-- Pagination -->
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">
                {{
                  $t("common.showing_range", {
                    from: fromRow,
                    to: toRow,
                    total: dataCount,
                  })
                }}
              </span>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              @click="getIncubatorTypesList"
            >
              <b-pagination
                v-model="page"
                :total-rows="dataCount"
                :per-page="take"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <!-- No permission state -->
    <div v-else class="text-center py-5 text-muted">
      {{ $t("common.no_permission") }}
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BFormTextarea,
  BTable,
  BPagination,
  BSpinner,
  BBadge,
  BCardText,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";

import permissionChecker from "@/mixins/permissionChecker";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";

export default {
  components: {
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BFormTextarea,
    BTable,
    BPagination,
    BSpinner,
    BBadge,
    BCardText,
    vSelect,
    ActionsButtons,
    DeleteModal,
  },
  mixins: [permissionChecker],

  data() {
    return {
      // paging
      take: 10,
      page: 1,
      dataCount: 0,
      loading: true,

      // list data
      data: [],

      // filters
      filters: {
        name: "",
        min_price: "",
      },

      // form
      editForm: {
        name: "",
        description: "",
        price_per_time_unit: "",
        time_unit: "day",
        _method: "PUT",
      },
      createMode: false,
      idItem: null,

      errorForm: {
        name: "",
        price_per_time_unit: "",
      },

      // columns
      tableColumns: [
        { key: "name", label: this.$t("common.name"), sortable: true },
        {
          key: "price_per_time_unit",
          label: this.$t("incubator.price_per_day"),
          sortable: true,
        },
        { key: "description", label: this.$t("common.description") },
        { key: "actions", label: this.$t("common.actions") },
      ],
    };
  },

  computed: {
    // permission-derived flags (no template-level store usage)
    canList() {
      return this.hasPermission("get-incubatorType");
    },
    canCreate() {
      return this.hasPermission("store-incubatorType");
    },
    canUpdate() {
      return this.hasPermission("update-incubatorType");
    },
    canDelete() {
      return this.hasPermission("delete-incubatorType");
    },

    fromRow() {
      if (!this.dataCount) return 0;
      return (this.page - 1) * this.take + 1;
    },
    toRow() {
      const to = this.page * this.take;
      return to > this.dataCount ? this.dataCount : to;
    },
  },

  watch: {
    page() {
      if (this.canList) {
        this.getIncubatorTypesList();
      }
    },
  },

  mounted() {
    this.getIncubatorTypesList();
  },

  methods: {

     // Format price with currency
     formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },
    /** unified permission helper (array | object | string) */
    hasPermission(key) {
      try {
        const store = this.$store;
        if (!store || !store.state || !store.state.app) return false;
        const perms = store.state.app.permissions;
        if (!perms) return false;

        if (Array.isArray(perms)) {
          return perms.indexOf(key) !== -1;
        }
        if (typeof perms === "object") {
          return !!perms[key];
        }
        if (typeof perms === "string") {
          return (
            perms
              .split(",")
              .map((s) => s.trim())
              .indexOf(key) !== -1
          );
        }
        return false;
      } catch (e) {
        return false;
      }
    },

    // money
    formatMoney(v) {
      const n = Number(v || 0);
      try {
        return new Intl.NumberFormat("ar-IQ", {
          maximumFractionDigits: 0,
        }).format(n);
      } catch (e) {
        return String(n);
      }
    },

    // LIST
    async getIncubatorTypesList() {
      this.loading = true;
      let url = `/incubatorTypes?page=${this.page}&take=${this.take}&sort=-id`;

      if (this.filters.name)
        url += `&filter[name]=${encodeURIComponent(this.filters.name)}`;
      if (this.filters.min_price !== "" && this.filters.min_price !== null) {
        url += `&filter[price_per_time_unit]=${encodeURIComponent(
          this.filters.min_price
        )}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    // OPEN MODAL
    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.idItem = null;
        this.editForm = {
          name: "",
          description: "",
          price_per_time_unit: 0,
          time_unit: "day",
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.idItem = item.id;
        this.editForm = {
          name: item.name || "",
          description: item.description || "",
          price_per_time_unit: item.price_per_time_unit || "",
          time_unit: "day",
          _method: "PUT",
        };
      }
      this.$bvModal.show("modal-edit");
    },

    // DELETE FLOW
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },

    deleteItem() {
      this.$store
        .dispatch("delete", `/incubatorTypes/${this.deleteForm.id}`)
        .then(() => {
          this.getIncubatorTypesList();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.delete_success"),
              icon: "success",
              variant: "info",
              text: this.$t("incubator.type_deleted", {
                name: this.deleteForm.name || "",
              }),
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("incubator.type_delete_error", {
                name: this.deleteForm.name || "",
              }),
            },
          });
        });
    },

    // SUBMIT (CREATE/UPDATE)
    submitItem() {
      this.loading = true;

      // reset errors
      this.errorForm.name = "";
      this.errorForm.price_per_time_unit = "";

      // build payload & clean empties
      const payload = { ...this.editForm };
      // Always set time_unit to "day"
      payload.time_unit = "day";
      
      for (const k in payload) {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      }
      
      // Re-add time_unit if it was deleted
      payload.time_unit = "day";

      // validations
      let invalid = false;
      if (!payload.name) {
        this.errorForm.name =
          this.$t("validation.required_name") || "The name field is required.";
        invalid = true;
      }
      if (
        payload.price_per_time_unit === undefined ||
        payload.price_per_time_unit === "" ||
        Number(payload.price_per_time_unit) < 0
      ) {
        this.errorForm.price_per_time_unit =
          this.$t("validation.required_price") ||
          "The price per day field is required.";
        invalid = true;
      }

      if (invalid) {
        this.loading = false;
        return;
      }

      if (this.createMode) {
        if (!this.canCreate) {
          this.loading = false;
          return;
        }
        const createParams = { form: payload, path: "/incubatorTypes" };
        this.$store
          .dispatch("create", createParams)
          .then(() => {
            this.getIncubatorTypesList();
            this.$bvModal.hide("modal-edit");
            this.loading = false;
          })
          .catch((error) => {
            this.loading = false;
            if (error.response && error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: this.$t("validation.error"),
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      } else {
        if (!this.canUpdate) {
          this.loading = false;
          return;
        }
        const updateParams = {
          form: payload,
          path: `/incubatorTypes/${this.idItem}`,
        };
        this.$store
          .dispatch("update", updateParams)
          .then(() => {
            this.getIncubatorTypesList();
            this.$bvModal.hide("modal-edit");
            this.loading = false;
          })
          .catch((error) => {
            this.loading = false;
            if (error.response && error.response.status === 400) {
              for (const item2 in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: this.$t("validation.error"),
                    variant: "danger",
                    text: error.response.data[item2][0],
                  },
                });
              }
            }
          });
      }
    },
  },
};
</script>

<style scoped>
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}
.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}
.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}
.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

/* Table horizontal scroll - always visible */
.table-responsive,
.b-table-responsive,
.table > .table-responsive {
  overflow-x: scroll !important;
  overflow-y: visible !important;
  -webkit-overflow-scrolling: touch;
}

/* Make scrollbar always visible and styled - Webkit browsers */
.table-responsive::-webkit-scrollbar,
.b-table-responsive::-webkit-scrollbar {
  height: 14px !important;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.table-responsive::-webkit-scrollbar-track,
.b-table-responsive::-webkit-scrollbar-track {
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 0 10px;
}

.table-responsive::-webkit-scrollbar-thumb,
.b-table-responsive::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 8px;
  border: 2px solid #f5f5f5;
}

.table-responsive::-webkit-scrollbar-thumb:hover,
.b-table-responsive::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}

/* For Firefox */
.table-responsive,
.b-table-responsive {
  scrollbar-width: auto !important;
  scrollbar-color: #888 #f5f5f5;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
